import { lazy, Suspense } from 'react';
import type { ComponentType } from 'react';
import * as reactRouterDom from 'react-router-dom';
import { Routes, Route } from 'react-router-dom';
import { getSuperTokensRoutesForReactRouterDom } from 'supertokens-auth-react/ui';
import { EmailPasswordPreBuiltUI } from 'supertokens-auth-react/recipe/emailpassword/prebuiltui';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import AdminRoute from 'lib/AdminRoute';
import ApiUserRoute from 'lib/ApiUserRoute';
import Loading from 'lib/Loading';
import ContentLayout from './components/ContentLayout';

// Every screen below is code-split with React.lazy so `bun run build` stops
// emitting one >500 kB chunk (see Phase 6 findings) -- each becomes its own
// chunk, fetched on first visit to that route. `suspended()` gives each Route's
// `element` its own <Suspense> boundary scoped to just that Outlet slot, so
// navigating between two lazy routes only flashes the loader in the content
// area -- Nav, the ContentLayout shell and the sidebar rail (none of them lazy)
// stay mounted throughout.
const About = lazy(() => import('./components/About'));
const ApiDocs = lazy(() => import('./components/ApiDocs'));
const Support = lazy(() => import('./components/Support'));
const Bitcoin = lazy(() => import('./components/Bitcoin'));
const Contact = lazy(() => import('./components/Contact'));
const CollegeNatsQualifiers2023 = lazy(() => import('./components/articles/CollegeNats2023'));
const Matchups = lazy(() => import('./components/Matchups'));
const News = lazy(() => import('./components/news/News'));
const NewsPost = lazy(() => import('./components/news/NewsPost'));
const Hello = lazy(() => import('./components/home/Hello'));
const Team = lazy(() => import('./components/team/Team'));
const TeamRosters = lazy(() => import('./components/team/Rosters'));
const Roster = lazy(() => import('./components/team/Roster'));
const RecentResults = lazy(() => import('./components/team/RecentResults'));
const TeamVideos = lazy(() => import('./components/team/TeamVideos'));
const Person = lazy(() => import('./components/person/Person'));
const Tournament = lazy(() => import('./components/tournament/Tournament'));
const Game = lazy(() => import('./components/Game'));
const Popular = lazy(() => import('./components/Popular'));
const Collision = lazy(() => import('./components/collision/Collision'));
const BugReport = lazy(() => import('./components/BugReport'));
const Admin = lazy(() => import('./components/admin/Admin'));
const Users = lazy(() => import('./components/admin/Users'));
const User = lazy(() => import('./components/admin/User'));
const Collisions = lazy(() => import('./components/admin/Collisions'));
const CollisionDetails = lazy(() => import('./components/admin/CollisionDetails'));
const EtlRuns = lazy(() => import('./components/admin/EtlRuns'));
const EtlRunDetail = lazy(() => import('./components/admin/EtlRunDetail'));
const BugReports = lazy(() => import('./components/admin/BugReports'));
const Database = lazy(() => import('./components/admin/Database'));
const MergeTeams = lazy(() => import('./components/admin/MergeTeams'));
const MergePlayers = lazy(() => import('./components/admin/MergePlayers'));
const VideoSources = lazy(() => import('./components/admin/VideoSources'));
const VideoList = lazy(() => import('./components/admin/VideoList'));
const Stats = lazy(() => import('./components/admin/Stats'));
const ApiTokens = lazy(() => import('./components/admin/ApiTokens'));
const Developer = lazy(() => import('./components/developer/Developer'));
const UserProfileForm = lazy(() => import('./components/profile/UserProfile'));
const VideoViewer = lazy(() => import('./components/VideoViewer'));
const PageNotFound = lazy(() => import('./components/404'));

function suspended(Component: ComponentType) {
    return (
        <Suspense fallback={<Loading />}>
            <Component />
        </Suspense>
    );
}

export default function AppRoutes() {
    return (
        <Routes>
            {getSuperTokensRoutesForReactRouterDom(reactRouterDom, [EmailPasswordPreBuiltUI])}
            <Route path="/" element={<ContentLayout />}>
                <Route path="/" element={suspended(Hello)} />
                <Route path="/about" element={suspended(About)} />
                {/* Public on purpose: the API gate's 401 body points developers
                    here, and they arrive unauthenticated by definition. */}
                <Route path="/api-docs" element={suspended(ApiDocs)} />
                <Route path="/loader" element={suspended(Loading)} />
                <Route path="/support" element={suspended(Support)} />
                <Route path="/bitcoin" element={suspended(Bitcoin)} />
                <Route path="/contact" element={suspended(Contact)} />
                <Route
                    path="/college-nats-qualifiers"
                    element={suspended(CollegeNatsQualifiers2023)}
                />
                <Route path="/matchup" element={suspended(Matchups)} />
                <Route path="/news" element={suspended(News)} />
                <Route path="/news/:slug" element={suspended(NewsPost)} />
                {/* <Route path="/results" element={<Search isResultsPanel={true} />} /> */}
                <Route path="/team/:slug/" element={suspended(Team)}>
                    <Route path="roster/" element={suspended(TeamRosters)}>
                        <Route path=":rosterId" element={suspended(Roster)} />
                    </Route>

                    <Route path="results" element={suspended(RecentResults)} />
                    <Route path="film" element={suspended(TeamVideos)} />
                </Route>
                <Route path="/person/:slug/" element={suspended(Person)}></Route>
                <Route path="/tournament/:slug" element={suspended(Tournament)} />
                <Route path="/game/:id" element={suspended(Game)} />
                <Route path="/popular" element={suspended(Popular)} />
                <Route
                    path="collisions/:slug"
                    element={
                        <SessionAuth>
                            <Suspense fallback={<Loading />}>
                                <Collision />
                            </Suspense>
                        </SessionAuth>
                    }
                />
                <Route
                    path="/bug-report"
                    element={
                        <SessionAuth>
                            <Suspense fallback={<Loading />}>
                                <BugReport />
                            </Suspense>
                        </SessionAuth>
                    }
                />
                <Route
                    path="/admin/"
                    element={
                        <AdminRoute>
                            <Suspense fallback={<Loading />}>
                                <Admin />
                            </Suspense>
                        </AdminRoute>
                    }>
                    <Route path="users" element={suspended(Users)} />
                    <Route path="user/:id" element={suspended(User)} />
                    <Route path="collisions" element={suspended(Collisions)} />
                    <Route path="collisions/:id" element={suspended(CollisionDetails)} />
                    <Route path="etl-runs" element={suspended(EtlRuns)} />
                    <Route path="etl-runs/:id" element={suspended(EtlRunDetail)} />
                    <Route path="bug-reports" element={suspended(BugReports)} />
                    <Route path="service-health" element={suspended(Database)}></Route>
                    <Route path="merge-teams" element={suspended(MergeTeams)}></Route>
                    <Route path="merge-players" element={suspended(MergePlayers)}></Route>
                    <Route path="video-sources" element={suspended(VideoSources)}></Route>
                    <Route path="videos" element={suspended(VideoList)}></Route>
                    <Route path="stats" element={suspended(Stats)}></Route>
                    <Route path="api-tokens" element={suspended(ApiTokens)} />
                </Route>
                <Route
                    path="/developer"
                    element={
                        <ApiUserRoute>
                            <Suspense fallback={<Loading />}>
                                <Developer />
                            </Suspense>
                        </ApiUserRoute>
                    }
                />
                <Route path="/profile" element={suspended(UserProfileForm)} />
                <Route path="/video/:id" element={suspended(VideoViewer)} />
            </Route>

            <Route path="*" element={suspended(PageNotFound)} />
        </Routes>
    );
}
